import {useCallback} from "react";
import {useRouteContext} from "@tanstack/react-router";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {
    columnIsExportable,
    fetchOttopilotRowsForExport,
    type OttopilotExportColumnPredicate,
} from "~/components/ottopilotDataViewer/exportFromDb";
import {exportRowsAsPdf, exportRowsAsPng} from "~/utilities/exportUtilities";
import {transformRowForExport} from "~/utilities/utilities";
import {launchNativeCsvDownload, prepareNativeCsvDownload, type NativeCsvDownloadConfig} from "~/utilities/nativeCsvDownload";

type ExportErrorHandler = (message: string) => void;
type CsvExportMode<T extends {id: Uuid}> =
    | {kind: "native"; config: NativeCsvDownloadConfig<T>}
    | {kind: "disabled"};

export function useOttopilotExportActions<T extends {id: Uuid}>({
    controller,
    pngFilename,
    pdfFilename,
    documentTitle,
    onError,
    columnIsExportableFn,
    csvExportMode,
}: {
    controller: OttopilotDataViewerController<T>;
    /** Required only when calling `onExportPng`. */
    pngFilename?: string;
    pdfFilename?: string;
    /** Used for PNG/PDF document titles; unused for CSV-only callers. */
    documentTitle?: string;
    onError: ExportErrorHandler;
    columnIsExportableFn?: OttopilotExportColumnPredicate<T>;
    csvExportMode?: CsvExportMode<T>;
}) {
    const {environment} = useRouteContext({from: "__root__"});
    const nativeCsvDownload = csvExportMode?.kind === "native" ? csvExportMode.config : undefined;
    const resolveExportableColumn = columnIsExportableFn ?? columnIsExportable;

    const launchNative = useCallback((selection: {selectedIds?: ReadonlyArray<Uuid>; selectedRowId?: Uuid} = {}) => {
        if (nativeCsvDownload === undefined) {
            onError("This row does not have a native CSV export action.");
            return;
        }
        try {
            const url = prepareNativeCsvDownload({environment: environment, config: nativeCsvDownload,
                controller: controller, columnIsExportable: resolveExportableColumn, ...selection});
            launchNativeCsvDownload(url);
        } catch (error_) {
            onError(error_ instanceof Error ? error_.message : "Could not prepare CSV download.");
        }
    }, [controller, environment, nativeCsvDownload, onError, resolveExportableColumn]);

    const fetchRows = useCallback(async () => {
        try {
            return await fetchOttopilotRowsForExport(controller, resolveExportableColumn);
        } catch (error_) {
            onError(error_ instanceof Error ? error_.message : "Could not fetch rows for export.");
            return null;
        }
    }, [controller, onError, resolveExportableColumn]);

    const onExportCsv = useCallback(() => {
        if (nativeCsvDownload === undefined) {
            onError("CSV export is unavailable for this page.");
            return;
        }
        launchNative();
    }, [launchNative, nativeCsvDownload, onError]);

    const onExportCsvForRowId = useCallback((id: Uuid) => {launchNative({selectedRowId: id});}, [launchNative]);
    const onExportCsvForSelectedUserIds = useCallback((ids: ReadonlyArray<Uuid>) => {launchNative({selectedIds: ids});}, [launchNative]);

    const onExportPng = useCallback(() => {
        if (pngFilename === undefined) {
            onError("PNG export is not configured.");
            return;
        }
        void (async () => {
            const pack = await fetchRows();
            if (pack === null) {
                return;
            }
            if (pack.loadedRows.length === 0) {
                onError("No rows match the current filters.");
                return;
            }
            try {
                const rowsToExport = pack.loadedRows.map((row) => transformRowForExport(row, pack.exportColumns));
                void exportRowsAsPng(rowsToExport, pngFilename, documentTitle ?? "Export").catch((error_) => {
                    onError(error_ instanceof Error ? error_.message : "Could not export PNG.");
                });
            } catch (error_) {
                onError(error_ instanceof Error ? error_.message : "Could not prepare PNG export.");
            }
        })();
    }, [documentTitle, fetchRows, onError, pngFilename]);

    const onExportPdf = useCallback(() => {
        void (async () => {
            const pack = await fetchRows();
            if (pack === null) {
                return;
            }
            if (pack.loadedRows.length === 0) {
                onError("No rows match the current filters.");
                return;
            }
            try {
                const rowsToExport = pack.loadedRows.map((row) => transformRowForExport(row, pack.exportColumns));
                exportRowsAsPdf(rowsToExport, pdfFilename ?? `${documentTitle ?? "Export"}.pdf`, documentTitle ?? "Export");
            } catch (error_) {
                onError(error_ instanceof Error ? error_.message : "Could not prepare PDF export.");
            }
        })();
    }, [documentTitle, fetchRows, onError, pdfFilename]);

    return {onExportCsv, onExportCsvForRowId, onExportCsvForSelectedUserIds, onExportPng, onExportPdf};
}
